// react
import React, { useMemo, useState } from "react";

import Button from "../../../../components/Button";
import Toggle from "../../../../components/Inputs/ToggleSwitches/ThumbSwitch";
// components
import Modal from "../../../../components/Modal";
import Spacer from "../../../../components/Spacer";
import Spinner from "../../../../components/Spinner";
import Text from "../../../../components/Text";
import { StaticToast } from "../../../../components/Toast";

// hooks
import useRequest from "../../../../hooks/useRequest";

// utils
import get from "../../../../utils/GetNested";

// dependencies
import PropTypes from "../../../../lib/prop-types";

// styles
import css from "./TeamsModals.module.scss";

const DisplayEditTeamSettingsModal = (props) => {
	const { data, handleClose, onSubmit } = props;
	const { teamID, teamDisplayName, teamStatus, settings } = data;

	const {
		data: channelData,
		isRequesting: requestingChannelData,
		revalidate: getChannelData,
	} = useRequest("getChannelsOnTeam", { filters: { teamID: data.teamID } }, { skipCache: false });

	const initialState = {
		teamStatus,
	};
	const [toggleState, setToggleState] = useState(initialState);

	useMemo(() => {
		if (data.teamID) {
			getChannelData({ filters: { teamID: data.teamID } });
		}
	}, [data.teamID]);

	const selectChannelNotificationSettings = (channelID) => {
		const channelSettings =
			get(settings, ["channels"], []).find((channel) => channel.channel_id === channelID) || {};
		return channelSettings;
	};

	useMemo(() => {
		if (channelData) {
			channelData.forEach((channel) => {
				const channelSettings = selectChannelNotificationSettings(channel.id);
				initialState[channel.id] = {
					sendCompanyFeedback: get(
						channelSettings,
						["notificationSettings", "teams-notification-company-feedback", "status"],
						"disabled"
					),
				};
			});
			setToggleState({ ...initialState, ...toggleState });
		}
	}, [channelData]);

	const [updatesMade, setUpdatesMade] = useState(false);
	const [updates, setUpdates] = useState({ teamID });

	const handleToggleTeamStatus = (newValue) => {
		const currentValue = toggleState.teamStatus;
		if (currentValue !== newValue) {
			setToggleState({ ...toggleState, teamStatus: newValue });
			setUpdatesMade(true);
			setUpdates({ ...updates, teamUpdates: { teamStatus: newValue } });
		}
	};

	const handleToggleChannelStatus = (newValue, channelID) => {
		const currentValue = toggleState[channelID].sendCompanyFeedback;
		if (currentValue !== newValue) {
			setToggleState({
				...toggleState,
				[channelID]: { sendCompanyFeedback: newValue },
			});
			setUpdatesMade(true);
			setUpdates({
				...updates,
				channelUpdates: {
					...get(updates, ["channelUpdates"], {}),
					[channelID]: { sendCompanyFeedback: newValue },
				},
			});
		}
	};

	const modalButtons = (
		<div className={css.modalButtons}>
			<Button size="large" color="green" disabled={!updatesMade} onClick={() => onSubmit(updates)}>
				{" "}
				Submit
			</Button>
		</div>
	);

	const content = (
		<div className={css.modalContainer}>
			<div className={css.modalTeamNameContainer}>
				<Text size="sub-heading" weight="bold">
					{teamDisplayName}
				</Text>
			</div>

			<div className={css.modalItem}>
				<div>
					<Text size="paragraph-1" weight="semi-bold">
						Active:
					</Text>
				</div>
				<Toggle
					isLoading={false}
					onChange={(val) => handleToggleTeamStatus(val ? "enabled" : "disabled")}
					width={30}
					value={get(toggleState, ["teamStatus"], "disabled") === "enabled"}
				/>
			</div>

			<div className={css.modalChannelWrapper}>
				<div className={css.settingLabel}>
					<Text size="paragraph-1" weight="semi-bold">
						Post Company Feedback in Channel:
					</Text>
				</div>
				{!requestingChannelData ? (
					<div>
						{channelData.map((channel) => (
							<div className={css.modalChannelSettings} key={channel.id}>
								<div>
									<Text size="paragraph-2" weight="regular">
										{get(channel, ["displayName"], "")}
									</Text>
								</div>
								<Toggle
									isLoading={false}
									onChange={(val) =>
										handleToggleChannelStatus(val ? "enabled" : "disabled", channel.id)
									}
									width={30}
									value={
										get(toggleState, [channel.id, "sendCompanyFeedback"], "disabled") === "enabled"
									}
								/>
							</div>
						))}
					</div>
				) : (
					<Spinner size={28} />
				)}
			</div>

			<Spacer height={2} />

			<div>
				<StaticToast
					contentStyle={{ fontSize: "50%" }}
					message='Uninstall the app in Microsoft Teams by visiting the "Manage
					team" page'
					size="small"
					type="info"
				/>
			</div>
		</div>
	);

	return <Modal content={content} buttons={modalButtons} handleClose={handleClose} size="small" />;
};

DisplayEditTeamSettingsModal.propTypes = {
	data: PropTypes.object.isRequired,
	handleClose: PropTypes.func.isRequired,
	onSubmit: PropTypes.func.isRequired,
	openResyncModal: PropTypes.func.isRequired,
	tenantID: PropTypes.string.isRequired,
};

export default DisplayEditTeamSettingsModal;
